import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MDBBtn, MDBIcon, MDBSpinner } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbContext } from '../../components/breadcrumb/BreadcrumbContext';
import {
  duplicateItemPath,
  getLanguageText,
  getLocalized,
  mediathekImageUrl,
  publicMediaPath,
} from '../../utils/mediathek';
import useMediaItemData from './hooks/useMediaItemData';
import useMediathekUpload from './hooks/useMediathekUpload';
import {
  EditorColumns,
  EditorHeader,
  EditorMain,
  EditorShell,
  StickyActionBar,
  revealElement,
  useBeforeUnload,
  useDirtyState,
  useSaveShortcut,
} from './MediathekEditorLayout';
import {
  ConfirmModal,
  DuplicateButton,
  ErrorList,
  StatusBadge,
  TypeBadge,
  seriesTypeIcon,
} from './MediathekShared';
import { ItemAddressPanel, ItemLegacyPanel, ItemPublishingPanel } from './item-editor/ItemAside';
import { ItemLinksSection, ItemMediaSection, ItemPeopleSection, ItemTextsSection } from './item-editor/ItemSections';
import useCopySource from './item-editor/useCopySource';

const ITEMS_PATH = '/admin/mediathek/items';

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Duplicate trigger that looks like DuplicateButton but asks first (unsaved changes get lost). */
function DuplicateWithConfirmButton({ onClick, disabled = false }) {
  const { t } = useTranslation();
  return (
    <MDBBtn color="primary" outline type="button" onClick={onClick} disabled={disabled}>
      <MDBIcon fas icon="copy" className="me-2" aria-hidden="true" />
      {t('Duplicate')}
    </MDBBtn>
  );
}

function MediaItemEditor({ itemId, routeSeriesId, copyFrom }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { setDynamicLabel } = useBreadcrumbContext();
  const {
    item,
    setItem,
    setField,
    setLocalizedField,
    loadedSeriesId,
    seriesList,
    currentSeries,
    linkedCompany,
    selectCompany,
    clearCompany,
    searchCompanies,
    isLoading,
    isLoadingSeries,
    loadError,
    isSaving,
    errors,
    loadVersion,
    save,
    deleteItem,
  } = useMediaItemData(itemId, routeSeriesId);
  const { getUpload, uploadImage, uploadMedia } = useMediathekUpload('EditMediaItem');
  const isEdit = Boolean(itemId);

  // Copy of a saved item (?copyFrom): prefilled once, after the hook's create reset
  const copy = useCopySource({
    copyFrom: isEdit ? '' : copyFrom,
    ready: !isEdit && !isLoading,
    targetSeriesId: routeSeriesId,
    setItem,
  });
  const copyLoading = copy.status === 'loading';

  const [lang, setLang] = useState('de');
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [revealTick, setRevealTick] = useState(0);

  // Snapshot after every load (loadVersion) and after the copy prefill (copyVersion)
  const { isDirty } = useDirtyState(item, `${loadVersion}-${copy.copyVersion}`, { warnOnUnload: isEdit });
  const titleDe = getLanguageText(item.title, 'de');
  const titleEn = getLanguageText(item.title, 'en');
  useBeforeUnload(!isEdit && isDirty && Boolean(titleDe.trim() || titleEn.trim()));

  const displayTitle = getLocalized(item.title, i18n.language);
  const seriesTitle = currentSeries ? getLocalized(currentSeries.title, i18n.language) : '';

  useEffect(() => {
    if (itemId && displayTitle) setDynamicLabel(':itemId', displayTitle);
  }, [itemId, displayTitle, setDynamicLabel]);

  useEffect(() => {
    if (routeSeriesId && seriesTitle) setDynamicLabel(':seriesId', seriesTitle);
  }, [routeSeriesId, seriesTitle, setDynamicLabel]);

  // Scroll to / focus the error list once it has rendered
  useEffect(() => {
    if (revealTick > 0) revealElement('me-errors');
  }, [revealTick]);

  const returnPath = location.state?.from
    || (routeSeriesId ? `/admin/mediathek/series/${routeSeriesId}` : ITEMS_PATH);

  const seriesOptions = useMemo(() => [
    { text: t('Select series'), value: '' },
    ...seriesList.map((s) => ({
      text: `${getLocalized(s.title, i18n.language) || s.slug || s.id} (${s.type === 'video' ? t('Video') : t('Podcast')})`,
      value: s.id,
    })),
  ], [seriesList, i18n.language, t]);

  const imageUpload = getUpload('image');
  const audioUpload = getUpload('audio');
  const videoUpload = getUpload('video');
  const anyUploadBusy = imageUpload.busy || audioUpload.busy || videoUpload.busy;
  const titleInvalid = saveAttempted && !titleDe.trim();

  const handleSave = async () => {
    if (isLoading || loadError || isSaving || anyUploadBusy || copyLoading) return;
    setSaveAttempted(true);
    const result = await save();
    if (result) {
      navigate(returnPath, { state: { toast: 'Item saved' } });
      return;
    }
    if (!titleDe.trim()) setLang('de');
    setRevealTick((n) => n + 1);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const ok = await deleteItem();
    setIsDeleting(false);
    setDeleteOpen(false);
    if (ok) navigate(returnPath, { state: { toast: 'Item deleted' } });
    else setRevealTick((n) => n + 1);
  };

  const handleImagePick = async (file) => {
    const image = await uploadImage(file, 'image');
    if (image) setField('image', { ...image, alt: item.image?.alt || '' });
  };

  const handleMediaPick = async (file, kind) => {
    const uploaded = await uploadMedia(file, kind);
    if (uploaded) setField(kind, uploaded);
  };

  const duplicatePath = isEdit ? duplicateItemPath({ id: itemId, mediaSeriesId: loadedSeriesId }) : null;
  // Leaving for the copy while a save / upload / delete runs would let the pending
  // save navigate away from the copy form (or silently abandon the upload)
  const duplicateBusy = isSaving || anyUploadBusy || isDeleting;
  // Chain the edit page's own origin to the copy so its Back / Save return there
  const returnFrom = location.state?.from;

  useSaveShortcut(
    handleSave,
    !isLoading && !loadError && !isSaving && !anyUploadBusy && !copyLoading && !isDeleting
      && !deleteOpen && !duplicateConfirmOpen,
  );

  if (isLoading) {
    return (
      <EditorShell>
        <div className="text-center py-5">
          <MDBSpinner color="primary" />
        </div>
      </EditorShell>
    );
  }

  if (loadError) {
    return (
      <EditorShell>
        <div className="card">
          <div className="me-empty" role="alert">
            <span className="me-empty-glyph" aria-hidden="true">
              <MDBIcon fas icon="exclamation-triangle" />
            </span>
            <p className="mb-0">{t(loadError)}</p>
            <Link to={ITEMS_PATH} className="btn btn-primary">{t('Back')}</Link>
          </div>
        </div>
      </EditorShell>
    );
  }

  const publicPath = publicMediaPath(currentSeries?.slug, item.slug);
  const dateText = formatDate(item.publishedAt);
  let headerTitle = titleDe || titleEn;
  if (!headerTitle) headerTitle = isEdit ? t('(untitled)') : t('New item');

  let headerActions = null;
  if (duplicatePath) {
    headerActions = isDirty
      ? <DuplicateWithConfirmButton onClick={() => setDuplicateConfirmOpen(true)} disabled={duplicateBusy} />
      : <DuplicateButton item={{ id: itemId, mediaSeriesId: loadedSeriesId }} from={returnFrom} disabled={duplicateBusy} />;
  }

  const sourceTitle = copy.source ? (getLocalized(copy.source.title, i18n.language) || t('(untitled)')) : '';

  return (
    <EditorShell>
      <EditorHeader
        thumbnailUrl={item.image?.fileName ? mediathekImageUrl(item.image.fileName) : ''}
        thumbnailIcon={seriesTypeIcon(currentSeries?.type)}
        eyebrow={currentSeries ? (
          <>
            <span className="text-truncate" style={{ minWidth: 0 }}>{seriesTitle || currentSeries.slug}</span>
            <TypeBadge type={currentSeries.type} />
          </>
        ) : (
          <span>{t('No series yet')}</span>
        )}
        title={headerTitle}
        meta={[
          item.episodeNumber !== '' && item.episodeNumber !== null && item.episodeNumber !== undefined
            && t('Episode {{n}}', { n: item.episodeNumber }),
          dateText && (
            <>
              <MDBIcon far icon="calendar-alt" aria-hidden="true" />
              <span className="visually-hidden">{t('Published at')}: </span>
              {dateText}
            </>
          ),
          publicPath && (
            <>
              <MDBIcon fas icon="link" aria-hidden="true" />
              <span className="visually-hidden">{t('Public URL')}: </span>
              {publicPath}
            </>
          ),
        ]}
        status={<StatusBadge status={item.status} />}
        actions={headerActions}
      />

      <div id="me-errors" className="me-focus-target">
        <ErrorList errors={errors} />
      </div>

      {copy.status === 'ready' && copy.source && (
        <div className="alert alert-info d-flex align-items-start gap-3" role="status">
          <MDBIcon fas icon="copy" className="mt-1" aria-hidden="true" />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div>
              {t('Copy of "{{title}}" – not saved yet. It is created as a draft with a new web address when you save.', { title: sourceTitle })}
            </div>
            {/* New tab: the (possibly already edited) copy on this page is kept */}
            <Link
              to={`/admin/mediathek/items/${encodeURIComponent(copy.source.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fw-semibold"
            >
              {t('Open original')}
              <span className="visually-hidden"> ({t('opens in a new tab')})</span>
            </Link>
          </div>
        </div>
      )}
      {copy.status === 'error' && (
        <div className="alert alert-warning d-flex align-items-start gap-3" role="alert">
          <MDBIcon fas icon="exclamation-triangle" className="mt-1" aria-hidden="true" />
          <div>{t('The original item could not be loaded. You can fill in the form manually.')}</div>
        </div>
      )}

      {copyLoading ? (
        <div className="card">
          <div className="card-body text-center py-5" role="status">
            <MDBSpinner color="primary" className="mb-3" role="presentation" />
            <div className="me-muted">{t('Loading the original item...')}</div>
          </div>
        </div>
      ) : (
        <EditorColumns
          asideLabel={t('Publishing')}
          aside={(
            <>
              <ItemPublishingPanel
                item={item}
                setField={setField}
                seriesOptions={seriesOptions}
                isLoadingSeries={isLoadingSeries}
                isSaving={isSaving}
              />
              <ItemAddressPanel
                item={item}
                itemId={itemId}
                setField={setField}
                currentSeries={currentSeries}
                isSaving={isSaving}
              />
              <ItemLegacyPanel item={item} />
            </>
          )}
        >
          <EditorMain>
            <ItemMediaSection
              item={item}
              seriesType={currentSeries?.type}
              uploads={{ image: imageUpload, audio: audioUpload, video: videoUpload }}
              setField={setField}
              onImagePick={handleImagePick}
              onMediaPick={handleMediaPick}
            />
            <ItemTextsSection
              item={item}
              lang={lang}
              onLangChange={setLang}
              titleInvalid={titleInvalid}
              setLocalizedField={setLocalizedField}
              editorKey={`item-content-${loadVersion}-${copy.copyVersion}`}
            />
            <ItemPeopleSection
              item={item}
              setField={setField}
              company={{ linkedCompany, selectCompany, clearCompany, searchCompanies }}
            />
            <ItemLinksSection item={item} setField={setField} />
          </EditorMain>
        </EditorColumns>
      )}

      <ConfirmModal
        open={deleteOpen}
        title={t('Delete item')}
        message={t('Are you sure you want to delete the item "{{title}}"? Audio and video files stay in storage.', { title: displayTitle })}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
        busy={isDeleting}
      />
      <ConfirmModal
        open={duplicateConfirmOpen}
        title={t('Duplicate item')}
        message={t('The copy is made from the saved version. Your unsaved changes on this page will be lost.')}
        confirmLabel={t('Discard changes and duplicate')}
        busy={duplicateBusy}
        onConfirm={() => {
          if (duplicateBusy) return;
          setDuplicateConfirmOpen(false);
          if (duplicatePath) navigate(duplicatePath, returnFrom ? { state: { from: returnFrom } } : undefined);
        }}
        onClose={() => setDuplicateConfirmOpen(false)}
      />

      <StickyActionBar
        mode={isEdit ? 'edit' : 'create'}
        isDirty={isDirty}
        uploadBusy={anyUploadBusy}
        errorCount={errors.length}
        onErrorsClick={() => revealElement('me-errors')}
        onDelete={isEdit ? () => setDeleteOpen(true) : undefined}
        onBack={() => navigate(returnPath)}
        onSave={handleSave}
        saving={isSaving}
        saveDisabled={copyLoading || isDeleting}
        deleteDisabled={isDeleting}
      />
    </EditorShell>
  );
}

/**
 * Item create / edit page. Routes: /admin/mediathek/items/:itemId (edit) and
 * /admin/mediathek/series/:seriesId/items/create[?copyFrom=<itemId>] (create,
 * optionally prefilled from a saved item). The editor is keyed by route + copy
 * source, so switching between items / a copy starts from a fresh state.
 */
export default function EditMediaItem() {
  const { itemId, seriesId } = useParams();
  const [searchParams] = useSearchParams();
  const copyFrom = itemId ? '' : (searchParams.get('copyFrom') || '');
  return (
    <MediaItemEditor
      key={`${itemId || 'new'}|${seriesId || ''}|${copyFrom}`}
      itemId={itemId}
      routeSeriesId={seriesId}
      copyFrom={copyFrom}
    />
  );
}
